import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Calendar, X } from 'lucide-react';
import { useApp } from '../../../contexts/AppContext';
import { formatNextRun } from '../../../utils/autoSyncSchedule';

export default function SyncActivityBanner({ onReview }) {
  const { state, markDesignsSeen } = useApp();
  const { designs, autoDispatch } = state;

  const flagged = designs.filter(d => d.is_new || d.is_updated);
  const flaggedCount = flagged.length;
  const newCount = flagged.filter(d => d.is_new).length;
  const updatedCount = flagged.filter(d => d.is_updated && !d.is_new).length;

  const showFlagged = flaggedCount > 0;
  const showSchedule = autoDispatch.enabled && autoDispatch.schedule;

  if (!showFlagged && !showSchedule) return null;

  const handleDismiss = () => {
    markDesignsSeen(flagged.map(d => d.design_id || d.id));
  };

  const handleReview = () => {
    if (onReview && flagged[0]) onReview(flagged[0].design_id || flagged[0].id);
  };

  const summaryText = (() => {
    if (newCount > 0 && updatedCount > 0) {
      return `${newCount} new, ${updatedCount} updated since your last visit`;
    }
    if (newCount > 0) return `${newCount} new design${newCount > 1 ? 's' : ''} since your last visit`;
    if (updatedCount > 0) return `${updatedCount} updated design${updatedCount > 1 ? 's' : ''} since your last visit`;
    return null;
  })();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.25 }}
        className="mb-6 squircle bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/10 border border-amber-200/60 dark:border-amber-700/40 px-4 py-3"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" strokeWidth={2} />
          </div>

          <div className="flex-1 min-w-0">
            {showFlagged && (
              <p className="text-body-sm font-medium text-amber-900 dark:text-amber-100">
                {summaryText}
              </p>
            )}
            {showSchedule && (
              <p className={`text-caption text-amber-800 dark:text-amber-300 ${showFlagged ? 'mt-1' : ''} flex items-center gap-1.5`}>
                <Calendar className="w-3 h-3" />
                Next auto-push: {formatNextRun(autoDispatch.schedule)}
              </p>
            )}
          </div>

          {showFlagged && (
            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                onClick={handleReview}
                className="px-3 py-1.5 squircle-sm text-body-sm font-medium text-amber-900 dark:text-amber-100 hover:bg-amber-200/60 dark:hover:bg-amber-700/30 transition-colors"
              >
                Review
              </button>
              <button
                onClick={handleDismiss}
                className="p-1.5 squircle-sm text-amber-700 dark:text-amber-400 hover:bg-amber-200/60 dark:hover:bg-amber-700/30 transition-colors"
                aria-label="Dismiss"
                title="Mark all as seen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
